import {
    Box,
    Flex,
    HStack,
    VStack,
    Text,
    Badge,
    Icon,
    Spinner,
    Center,
    Button,
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiCheck } from 'react-icons/fi';
import { FaExclamationTriangle } from 'react-icons/fa';
import { TbExternalLink, TbFileText } from 'react-icons/tb';
import type { OrderFeed } from '../hooks/useExportsFeed';
import { useGetEms } from '../../../hooks/useGetEms';
import { useGetOrderDetail, type OrderEmsPush } from '../../../hooks/useGetOrderDetail';
import EmsExportDayTrack from '../../../components/order/EmsExportDayTrack';
import EMSDetailModal from '../../../modals/EMSDetailModal';
import { Vin } from '../../../components/orders/components';

/**
 * Exports "By RO" view (master–detail): LHS lists the same EMS feed rows,
 * grouped one-per-RO by selection; RHS shows that RO's full export ledger —
 * the EmsExportDayTrack diff-first history, day-stubbed and collapsed until
 * a row is opened (any number can be open at once).
 *
 * No dedicated endpoint: the feed row's EMS id resolves to its order via
 * GET /ems/detail (`orderId`), and POST /order/detail already returns `ems.pushes` — the
 * exact payload the Export History drawer renders on the order page.
 */

interface ExportsByRoLedgerProps {
    orders: Array<OrderFeed>;
    searchQuery: string;
    /** LHS footer slot — the feed's pagination control. */
    footer?: React.ReactNode;
}

function formatReceived(received: Date): string {
    const orderDate = new Date(received);
    const isToday = orderDate.toDateString() === new Date().toDateString();
    if (isToday) {
        return orderDate
            .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
            .toLowerCase();
    }
    return orderDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
}

const StatusBadge = ({ status }: { status: OrderFeed['status'] }) => (
    <Badge
        bg={status === 'pending' ? 'yellow.500' : 'blue.500'}
        color="white"
        px={2}
        py={0.5}
        fontSize="10px"
        fontWeight="semibold"
        borderRadius="lg"
        display="inline-flex"
        alignItems="center"
        gap={1}
        flexShrink={0}
    >
        <Icon
            as={(status === 'pending' ? FaExclamationTriangle : FiCheck) as React.ElementType}
            w={2.5}
            h={2.5}
        />
        {status}
    </Badge>
);

/** RHS: EMS → workfile pushes → fully expanded day-track ledger. */
const RoLedgerPanel = ({ emsId }: { emsId: string }) => {
    const navigate = useNavigate();
    const [isEmsModalOpen, setIsEmsModalOpen] = useState(false);
    const { data: ems, isLoading: isEmsLoading } = useGetEms(emsId, !!emsId);
    const orderId = ems?.orderId;
    /** GET /ems/detail now returns the workfile ledger directly; the order fetch is a fallback for a stale API. */
    const hasApiPushes = Boolean(ems?.pushes?.length);
    const { data: orderDetail, isLoading: isOrderLoading } = useGetOrderDetail(orderId ?? '', {
        enabled: !!orderId && !hasApiPushes,
    });

    const pushes: Array<OrderEmsPush> | null = useMemo(() => {
        if (!ems) return null;
        if (ems.pushes?.length) return ems.pushes;
        if (orderId) return orderDetail?.ems.pushes ?? null;
        // Last resort (stale API, no order): the selected EMS is its own single-entry ledger.
        return [{
            _id: emsId,
            timestamp: new Date(ems.timeReceived).toISOString(),
            wasSubmitted: ems.status === 'submitted',
            isLatest: true,
        }];
    }, [ems, orderId, orderDetail, emsId]);

    if (isEmsLoading) {
        return (
            <Center h="100%">
                <Spinner size="lg" color="brand.500" />
            </Center>
        );
    }

    if (!ems) {
        return (
            <Center h="100%">
                <Text fontSize="sm" color="gray.500">
                    Could not load this EMS. Try refreshing.
                </Text>
            </Center>
        );
    }

    return (
        <Flex direction="column" h="100%" minH={0}>
            {/* RO header */}
            <Flex
                px={6}
                py={4}
                align="center"
                justify="space-between"
                gap={4}
                borderBottom="1px solid"
                borderColor="gray.200"
                flexShrink={0}
                flexWrap="wrap"
            >
                <VStack align="flex-start" spacing={1} minW={0}>
                    <HStack spacing={2.5} align="center">
                        <Text fontSize="lg" fontWeight="bold" color="gray.900" noOfLines={1}>
                            {ems.title}
                        </Text>
                        <StatusBadge status={ems.status} />
                    </HStack>
                    <HStack spacing={1.5} color="gray.500" fontSize="sm" flexWrap="wrap">
                        <Text>
                            RO #: <Text as="span" color="gray.800" fontWeight="semibold">{ems.roNumber || '—'}</Text>
                        </Text>
                        <Text color="gray.300">·</Text>
                        <Vin vin={ems.vin} />
                        <Text color="gray.300">·</Text>
                        <Text>
                            Shop: <Text as="span" color="gray.800" fontWeight="semibold">{ems.location}</Text>
                        </Text>
                    </HStack>
                </VStack>
                <HStack spacing={2} flexShrink={0}>
                    <Button
                        size="sm"
                        variant="outlineGray"
                        leftIcon={<Icon as={TbFileText as React.ElementType} boxSize={4} />}
                        onClick={() => setIsEmsModalOpen(true)}
                    >
                        View EMS
                    </Button>
                    {orderId && (
                        <Button
                            size="sm"
                            variant="outlineGray"
                            leftIcon={<Icon as={TbExternalLink as React.ElementType} boxSize={4} />}
                            onClick={() => navigate(`/order/${orderId}`)}
                        >
                            Open order
                        </Button>
                    )}
                </HStack>
            </Flex>

            {/* Ledger */}
            <Box flex={1} minH={0} overflowY="auto" px={6} py={4}>
                {pushes ? (
                    <EmsExportDayTrack pushes={pushes} multiOpen />
                ) : isOrderLoading ? (
                    <Center py={16}>
                        <Spinner size="lg" color="brand.500" />
                    </Center>
                ) : (
                    <Center py={16}>
                        <Text fontSize="sm" color="gray.500">
                            Could not load the export ledger for this RO.
                        </Text>
                    </Center>
                )}
            </Box>

            {isEmsModalOpen && (
                <EMSDetailModal
                    isOpen={isEmsModalOpen}
                    onClose={() => setIsEmsModalOpen(false)}
                    emsId={emsId}
                />
            )}
        </Flex>
    );
};

const ExportsByRoLedger = ({ orders, searchQuery, footer }: ExportsByRoLedgerProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedEmsId = searchParams.get('ro');
    const rowRefs = useRef(new Map<string, HTMLDivElement>());

    const selectRow = (id: string) => {
        setSearchParams(
            (previous) => {
                const next = new URLSearchParams(previous);
                next.set('ro', id);
                return next;
            },
            { replace: true },
        );
    };

    // Master–detail convention: land on the first RO when nothing is selected.
    useEffect(() => {
        if (!selectedEmsId && orders.length > 0) selectRow(orders[0]._id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEmsId, orders]);

    /** Arrow keys walk the RO list (roving focus) instead of scrolling the pane. */
    const moveSelection = (delta: 1 | -1) => {
        if (orders.length === 0) return;
        const currentIndex = orders.findIndex((order) => order._id === selectedEmsId);
        const nextIndex = currentIndex === -1
            ? (delta === 1 ? 0 : orders.length - 1)
            : Math.min(orders.length - 1, Math.max(0, currentIndex + delta));
        const next = orders[nextIndex];
        if (!next || next._id === selectedEmsId) return;
        selectRow(next._id);
        requestAnimationFrame(() => {
            const row = rowRefs.current.get(next._id);
            row?.focus({ preventScroll: true });
            row?.scrollIntoView({ block: 'nearest' });
        });
    };

    // Window-level so arrows work without first clicking into the list; typing
    // surfaces (search box, selects, modals) keep their native arrow behavior.
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
            const target = event.target as HTMLElement | null;
            if (target) {
                const tag = target.tagName;
                if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) return;
                if (target.closest('[role="dialog"], .chakra-modal__content, [role="menu"], [role="listbox"]')) return;
            }
            event.preventDefault();
            moveSelection(event.key === 'ArrowDown' ? 1 : -1);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    });

    if (orders.length === 0) {
        return (
            <Flex flex={1} align="center" justify="center" borderTop="1px solid" borderColor="gray.200">
                <VStack spacing={2}>
                    <Text fontSize="md" color="gray.500">
                        {searchQuery ? 'No matching orders found' : 'No orders found'}
                    </Text>
                    <Text fontSize="sm" color="gray.400">
                        {searchQuery ? 'Try a different search term' : 'Orders will appear here once you upload EMS files'}
                    </Text>
                </VStack>
            </Flex>
        );
    }

    return (
        <Flex flex={1} minH={0} borderTop="1px solid" borderColor="gray.200">
            {/* LHS — RO list */}
            <Flex
                w="360px"
                flexShrink={0}
                direction="column"
                minH={0}
                borderRight="1px solid"
                borderColor="gray.200"
            >
                <Box flex={1} minH={0} overflowY="auto">
                    {orders.map((order) => {
                        const isSelected = order._id === selectedEmsId;
                        return (
                            <Flex
                                key={order._id}
                                ref={(el: HTMLDivElement | null) => {
                                    if (el) {
                                        rowRefs.current.set(order._id, el);
                                    } else {
                                        rowRefs.current.delete(order._id);
                                    }
                                }}
                                role="button"
                                tabIndex={0}
                                align="center"
                                gap={3}
                                px={4}
                                py={3}
                                cursor="pointer"
                                bg={isSelected ? 'blue.50' : 'transparent'}
                                boxShadow={isSelected ? 'inset 3px 0 0 0 var(--chakra-colors-brand-500)' : undefined}
                                borderBottom="1px solid"
                                borderColor="gray.100"
                                _hover={{ bg: isSelected ? 'blue.50' : 'gray.50' }}
                                _focusVisible={{ boxShadow: 'focus', outline: 'none' }}
                                onClick={() => selectRow(order._id)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        selectRow(order._id);
                                    }
                                }}
                            >
                                <VStack align="flex-start" spacing={0.5} flex={1} minW={0}>
                                    <Text
                                        fontSize="sm"
                                        fontWeight={isSelected ? 'semibold' : 'medium'}
                                        color="gray.800"
                                        noOfLines={1}
                                    >
                                        {order.title}
                                    </Text>
                                    <Vin vin={order.vin} />
                                    <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                        {order.location} · {formatReceived(order.received)}
                                    </Text>
                                </VStack>
                                <VStack align="flex-end" spacing={1} flexShrink={0}>
                                    <StatusBadge status={order.status} />
                                    <Text fontSize="xs" color="gray.500">
                                        {order.numParts} part{order.numParts === 1 ? '' : 's'}
                                    </Text>
                                </VStack>
                            </Flex>
                        );
                    })}
                </Box>
                {footer ? (
                    <Box flexShrink={0} borderTop="1px solid" borderColor="gray.200">
                        {footer}
                    </Box>
                ) : null}
            </Flex>

            {/* RHS — export ledger for the selected RO */}
            <Box flex={1} minW={0} minH={0} bg="white">
                {selectedEmsId ? (
                    <RoLedgerPanel key={selectedEmsId} emsId={selectedEmsId} />
                ) : (
                    <Center h="100%">
                        <Text fontSize="sm" color="gray.500">
                            Select an RO to see its export history.
                        </Text>
                    </Center>
                )}
            </Box>
        </Flex>
    );
};

export default ExportsByRoLedger;
